import { supabase } from './supabase';
import { PostgrestError } from '@supabase/supabase-js';

/**
 * Execute RPC function
 */
export async function callRPC<T = any>(
  functionName: string,
  params?: Record<string, any>
): Promise<T> {
  const { data, error } = await supabase.rpc(functionName, params || {});

  if (error) {
    console.error(`RPC Error [${functionName}]:`, error);
    throw error;
  }
  
  return data as T;
}

/**
 * Select from table
 */
export async function selectFrom<T = any>(
  table: string,
  query?: {
    select?: string;
    eq?: Record<string, any>;
    in?: Record<string, any[]>;
    neq?: Record<string, any>;
    gt?: Record<string, any>;
    gte?: Record<string, any>;
    lt?: Record<string, any>;
    lte?: Record<string, any>;
    like?: Record<string, string>;
    ilike?: Record<string, string>;
    order?: { column: string; ascending?: boolean };
    limit?: number;
    range?: { from: number; to: number };
  }
): Promise<T[]> {
  let queryBuilder = supabase.from(table).select(query?.select || '*');

  if (query?.eq) {
    Object.entries(query.eq).forEach(([key, value]) => {
      queryBuilder = queryBuilder.eq(key, value);
    });
  }

  if (query?.neq) {
    Object.entries(query.neq).forEach(([key, value]) => {
      queryBuilder = queryBuilder.neq(key, value);
    });
  }

  if (query?.in) {
    Object.entries(query.in).forEach(([key, values]) => {
      queryBuilder = queryBuilder.in(key, values);
    });
  }

  if (query?.gt) {
    Object.entries(query.gt).forEach(([key, value]) => {
      queryBuilder = queryBuilder.gt(key, value);
    });
  }

  if (query?.gte) {
    Object.entries(query.gte).forEach(([key, value]) => {
      queryBuilder = queryBuilder.gte(key, value);
    });
  }

  if (query?.lt) {
    Object.entries(query.lt).forEach(([key, value]) => {
      queryBuilder = queryBuilder.lt(key, value);
    });
  }

  if (query?.lte) {
    Object.entries(query.lte).forEach(([key, value]) => {
      queryBuilder = queryBuilder.lte(key, value);
    });
  }

  if (query?.like) {
    Object.entries(query.like).forEach(([key, value]) => {
      queryBuilder = queryBuilder.like(key, value);
    });
  }

  if (query?.ilike) {
    Object.entries(query.ilike).forEach(([key, value]) => {
      queryBuilder = queryBuilder.ilike(key, value);
    });
  }

  if (query?.order) {
    queryBuilder = queryBuilder.order(
      query.order.column,
      { ascending: query.order.ascending ?? true }
    );
  }

  if (query?.limit) {
    queryBuilder = queryBuilder.limit(query.limit);
  }

  if (query?.range) {
    queryBuilder = queryBuilder.range(query.range.from, query.range.to);
  }

  const { data, error } = await queryBuilder;

  if (error) {
    console.error(`Query Error [${table}]:`, error);
    throw error;
  }
  
  return data as T[];
}

/**
 * Select single record from table
 */
export async function selectOne<T = any>(
  table: string,
  filter: Record<string, any>,
  select?: string
): Promise<T | null> {
  let queryBuilder = supabase.from(table).select(select || '*');

  Object.entries(filter).forEach(([key, value]) => {
    queryBuilder = queryBuilder.eq(key, value);
  });

  const { data, error } = await queryBuilder.single();

  if (error) {
    // Return null if not found (404), throw other errors
    if (error.code === 'PGRST116') {
      return null;
    }
    console.error(`Query Error [${table}]:`, error);
    throw error;
  }
  
  return data as T;
}

/**
 * Insert into table
 */
export async function insertInto<T = any>(
  table: string,
  values: T | T[]
): Promise<T[]> {
  const { data, error } = await supabase
    .from(table)
    .insert(values)
    .select();

  if (error) {
    console.error(`Insert Error [${table}]:`, error);
    throw error;
  }
  
  return data as T[];
}

/**
 * Update table
 */
export async function updateTable<T = any>(
  table: string,
  values: Partial<T>,
  filter: Record<string, any>
): Promise<T[]> {
  let queryBuilder = supabase.from(table).update(values);

  Object.entries(filter).forEach(([key, value]) => {
    queryBuilder = queryBuilder.eq(key, value);
  });

  const { data, error } = await queryBuilder.select();

  if (error) {
    console.error(`Update Error [${table}]:`, error);
    throw error;
  }
  
  return data as T[];
}

/**
 * Delete from table
 */
export async function deleteFrom(
  table: string,
  filter: Record<string, any>
): Promise<void> {
  let queryBuilder = supabase.from(table).delete();

  Object.entries(filter).forEach(([key, value]) => {
    queryBuilder = queryBuilder.eq(key, value);
  });

  const { error } = await queryBuilder;

  if (error) {
    console.error(`Delete Error [${table}]:`, error);
    throw error;
  }
}

/**
 * Upsert (insert or update)
 */
export async function upsert<T = any>(
  table: string,
  values: T | T[],
  onConflict?: string
): Promise<T[]> {
  let queryBuilder = supabase.from(table).upsert(values);

  if (onConflict) {
    queryBuilder = queryBuilder.eq(onConflict, onConflict);
  }

  const { data, error } = await queryBuilder.select();

  if (error) {
    console.error(`Upsert Error [${table}]:`, error);
    throw error;
  }
  
  return data as T[];
}

